import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, Code } from "lucide-react";
import { Button } from "../ui/button";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
  viewport: { once: true },
};

const sampleEmailHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>qwip</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Funnel+Display:wght@400;700&family=Cormorant+Garamond:ital@1&family=DM+Sans:wght@400;500;600;700&display=swap');
    body { margin: 0; padding: 0; background-color: #f2f0ec; font-family: 'DM Sans', Arial, sans-serif; color: #1f3d32; -webkit-font-smoothing: antialiased; }
    .outer { padding: 40px 16px; }
    .card { max-width: 560px; margin: 0 auto; background: #FDFBF7; border-radius: 12px; overflow: hidden; }
    .inner { padding: 44px 40px 36px; }
    .logo { font-family: 'Funnel Display', sans-serif; font-size: 32px; font-weight: 700; color: #1f3d32; text-decoration: none; display: block; margin-bottom: 28px; }
    .divider { height: 1px; background: #c8ddd2; margin-bottom: 32px; }
    h1 { font-family: 'Funnel Display', sans-serif; font-size: 26px; font-weight: 700; margin: 0 0 12px; color: #1f3d32; line-height: 1.2; }
    p { font-size: 15px; line-height: 1.7; color: #4a6b5c; margin: 0 0 16px; }
    .code-block { font-family: 'Funnel Display', sans-serif; font-size: 36px; font-weight: 700; letter-spacing: 8px; color: #1f3d32; margin: 28px 0; text-align: center; background: #f0ede7; border-radius: 10px; padding: 20px; }
    .btn { display: inline-block; background: #1f3d32; color: #ffffff !important; padding: 14px 32px; border-radius: 8px; text-decoration: none; font-weight: 600; font-size: 14px; font-family: 'DM Sans', sans-serif; }
    .accent { font-style: italic; font-family: 'Cormorant Garamond', Georgia, serif; font-size: 17px; color: #1f3d32; }
    .secondary-link { display: inline-block; margin-top: 14px; font-size: 14px; color: #1f3d32; text-decoration: underline; }
    .footer-bar { background: #1f3d32; padding: 24px 40px; }
    .footer-bar p { color: #a3c4b3; font-size: 12px; margin: 0 0 4px; }
    .footer-bar a { color: #a3c4b3; text-decoration: underline; }
    .footer-bar .brand { font-family: 'Funnel Display', sans-serif; color: #ffffff; font-size: 16px; font-weight: 700; margin-bottom: 8px; }
  </style>
</head>
<body>
  <div class="outer">
    <div class="card">
      <div class="inner">
        <a href="https://qwipsite.lovable.app" class="logo">qwip</a>
        <div class="divider"></div>
        <h1>how was the pod?</h1>
        <p>Hey {{name}},</p>
        <p>Saw you tried out a pod the other day - thank you for giving us a shot.</p>
        <p>We'd genuinely love to hear what you thought. The good, the awkward, the <span class="accent">"this would've been better if..."</span> - all of it helps us build something people actually want to keep using.</p>
        <p>Just hit reply. A real human (one of us) reads every single one, and as a thank you we'll knock a discount off your next session.</p>
        <div style="text-align:center; margin: 28px 0 8px;">
          <a href="mailto:hello@qwip.co.uk?subject=My%20pod%20feedback" class="btn">Reply with feedback</a><br>
          <a href="https://wa.me/447347474059" class="secondary-link">Or message us on WhatsApp</a>
        </div>
      </div>
      <div class="footer-bar">
        <p class="brand">qwip</p>
        <p>the modern public phone booth.</p>
        <p style="margin-top: 12px;"><a href="#">Unsubscribe</a>&nbsp;&nbsp;·&nbsp;&nbsp;<a href="#">Privacy Policy</a></p>
      </div>
    </div>
  </div>
</body>
</html>`;

const EmailTemplateSection = () => {
  const [copiedHtml, setCopiedHtml] = useState(false);
  const [showCode, setShowCode] = useState(false);

  const copyHtml = async () => {
    await navigator.clipboard.writeText(sampleEmailHtml);
    setCopiedHtml(true);
    setTimeout(() => setCopiedHtml(false), 2000);
  };

  return (
    <motion.div {...fadeIn} className="mb-28">
      <h2 className="text-2xl font-semibold mb-10 text-muted-foreground">Email Template</h2>
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-border flex-wrap gap-3">
          <p className="text-sm text-muted-foreground">Brand-compliant email template — Funnel Display headings, green footer bar, qwip tone.</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShowCode(!showCode)}>
              <Code size={14} />
              {showCode ? "Preview" : "View HTML"}
            </Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={copyHtml}>
              {copiedHtml ? <Check size={14} /> : <Copy size={14} />}
              {copiedHtml ? "Copied!" : "Copy HTML"}
            </Button>
          </div>
        </div>

        {showCode ? (
          <pre className="p-6 text-xs font-mono text-muted-foreground overflow-x-auto max-h-[500px] overflow-y-auto bg-muted/30">
            <code>{sampleEmailHtml}</code>
          </pre>
        ) : (
          <div className="p-6 bg-muted/30">
            <div className="max-w-[600px] mx-auto rounded-xl overflow-hidden shadow-sm">
              <iframe
                srcDoc={sampleEmailHtml}
                title="Email template preview"
                className="w-full border-0"
                style={{ height: "700px" }}
                sandbox=""
              />
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default EmailTemplateSection;
